// «Сходимость» hero — KPI cards + honesty partition + time-to-threshold
// histogram. Mirrors the Точность-24h SummaryCards/bucket-distribution
// vocabulary (same .summary-* / .bucket-* classes) so the two tabs read
// identically; the numbers are convergence-specific and threshold-reactive.
//
// Data:
//   summary  ← ConvergenceAggregateResponse.summary  (api/types.ts)
//   kpi      ← timeToThreshold(config.points, threshold) computed by the
//              parent (ConvergenceTab) from the aggregate, passed in so the
//              KPI + curve share one calculation.
//   buckets  ← per-cell time-to-threshold distribution, derived client-side
//              from the heatmap by ConvergenceTab (no backend endpoint).

import type { ConvergenceAggregateSummary } from '../../api/types';
import { formatConfig, formatObsDate } from './convergenceScales';

export interface TimeBucket {
  /** e.g. "3–6 ч" */
  range: string;
  count: number;
  pct: number;
}

export interface ConvergenceKpi {
  /** median hours-to-threshold across the enabled configs, or null */
  medianTime: number | null;
  /** fastest-converging enabled config key + its hours, or null */
  bestKey: string | null;
  bestTime: number | null;
  /** slowest-converging enabled config key + its hours (null = never) */
  slowKey: string | null;
  slowTime: number | null;
  /** count of enabled configs (for the «по N вкл. конфигам» caption) */
  enabledCount: number;
}

const CONFIG_COLOR_VAR: Record<string, string> = {
  all4: 'var(--ink)',
  gps: 'var(--gps)',
  gal: 'var(--gal)',
  glo: 'var(--glo)',
  bds: 'var(--bds)',
};
const configColor = (key: string): string =>
  CONFIG_COLOR_VAR[key] ?? 'var(--ink)';

/** Scroll the «порог» control (in ConvergenceCurve) into view — the KPI cards
 *  read off that threshold, so the hint links the operator straight to it. */
function focusThresholdControl(): void {
  document
    .getElementById('conv-threshold-control')
    ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

export function ConvergenceSummary({
  summary,
  threshold,
  kpi,
  buckets,
}: {
  summary: ConvergenceAggregateSummary;
  threshold: number;
  kpi: ConvergenceKpi;
  buckets: TimeBucket[];
}) {
  const total =
    summary.with_result +
    summary.no_reference +
    summary.in_progress +
    summary.not_computed;
  const pct = (n: number): number => (total > 0 ? (n / total) * 100 : 0);

  return (
    <div className="analytics-summary">
      <div className="summary-cards-grid conv-summary-grid">
        <div className="summary-card">
          <div className="summary-label">всего серий</div>
          <div className="summary-value">{summary.series}</div>
          <div className="summary-sub">
            {summary.stations} станций · {summary.with_result} с эталоном
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-label">медиана до {threshold} мм</div>
          <div className="summary-value">
            {kpi.medianTime == null ? '—' : kpi.medianTime.toFixed(1)}
            <span className="unit">ч</span>
          </div>
          <div className="summary-sub">
            по {kpi.enabledCount} вкл. конфигам ·{' '}
            <button type="button" className="conv-thr-link" onClick={focusThresholdControl}>
              порог {threshold} мм ⚙
            </button>
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-label">быстрее сходится</div>
          {kpi.bestKey ? (
            <>
              <div className="summary-value-small">
                <span className="cd" style={{ background: configColor(kpi.bestKey) }} />
                {formatConfig(kpi.bestKey)}
              </div>
              <div className="summary-sub">
                первым достигает {threshold} мм · {kpi.bestTime!.toFixed(1)} ч
              </div>
            </>
          ) : (
            <div className="summary-micro">
              {kpi.enabledCount > 0
                ? `ни один конфиг не достигает ${threshold} мм — увеличьте порог`
                : 'включите конфиг ниже'}
            </div>
          )}
        </div>

        <div className="summary-card">
          <div className="summary-label">медленнее сходится</div>
          {kpi.slowKey ? (
            <>
              <div className="summary-value-small">
                <span className="cd" style={{ background: configColor(kpi.slowKey) }} />
                {formatConfig(kpi.slowKey)}
              </div>
              <div className="summary-sub">
                {kpi.slowTime == null
                  ? `последним · не достигает ${threshold} мм`
                  : `последним достигает ${threshold} мм · ${kpi.slowTime.toFixed(1)} ч`}
              </div>
            </>
          ) : (
            <div className="summary-micro">—</div>
          )}
        </div>
      </div>

      {/* KPI criterion + threshold link — «быстрее/медленнее» is decided on the
          MEDIAN curve crossing the operator threshold. */}
      <button type="button" className="conv-kpi-explain" onClick={focusThresholdControl}>
        «быстрее / медленнее» = чья медианная кривая первой / последней опускается
        до порога {threshold} мм · ⚙ настроить порог
      </button>

      {/* Honesty partition — with_result / no_reference / not_computed.
          NEVER fold «без эталона» into measured accuracy. */}
      <div className="conv-honesty">
        <div className="conv-honesty-bar">
          <i className="h-ok" style={{ width: `${pct(summary.with_result)}%` }} />
          <i className="h-noref" style={{ width: `${pct(summary.no_reference)}%` }} />
          <i className="h-wip" style={{ width: `${pct(summary.in_progress)}%` }} />
          <i className="h-fail" style={{ width: `${pct(summary.not_computed)}%` }} />
        </div>
        <div className="conv-honesty-keys">
          <span className="conv-hkey ok">
            <span className="gl">●</span>
            <span className="n">{summary.with_result}</span> с результатом
          </span>
          <span className="conv-hkey noref">
            <span className="gl">○</span>
            <span className="n">{summary.no_reference}</span> без эталона
          </span>
          <span className="conv-hkey wip">
            <span className="gl">⏳</span>
            <span className="n">{summary.in_progress}</span> в работе
          </span>
          <span className="conv-hkey fail">
            <span className="gl">✕</span>
            <span className="n">{summary.not_computed}</span> не вычислено
          </span>
        </div>
      </div>

      {/* Distribution of per-cell time-to-threshold (client-derived). */}
      {buckets.length > 0 && (
        <div className="conv-hist">
          <div className="conv-hist-cap">
            сколько станций×конфигов достигают порога {threshold} мм за это время
            наблюдений
          </div>
          <div
            className="bucket-distribution"
            role="list"
            aria-label="Распределение времени до порога"
          >
            {buckets.map((b) => (
              <div
                className={`bucket-row${b.range.startsWith('не достигает') ? ' conv-bucket-never' : ''}`}
                key={b.range}
                role="listitem"
              >
                <div className="bucket-label-left">{b.range}</div>
                <div className="bucket-bar-track">
                  <div className="bucket-bar-fill" style={{ width: `${b.pct}%` }} />
                </div>
                <div className="bucket-label-right">
                  {b.count} <span className="bucket-pct">({b.pct}%)</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {summary.latest_obs_date && (
        <div className="conv-summary-foot">
          обновлено по наблюдениям до {formatObsDate(summary.latest_obs_date)}
        </div>
      )}
    </div>
  );
}
